import { useApolloClient } from "@apollo/client";
import { CardContainer } from "@msys/ui";
import { Description as DescriptionIcon } from "@mui/icons-material";
import { Button, List } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../../../commons/Avatar.js";
import { ProfileListItemButton } from "../../../commons/ProfileListItem.js";
import { CrmCreateFormPrefill } from "../../../features/crm/CrmCreateForm.js";
import { CreateProjectModal } from "../../../features/projects/modals/CreateProjectModal.js";
import { PendingDocSharePreviewModal } from "./PendingDocSharePreviewModal.js";
import {
  PendingDocSharesBox_PendingDocShareFragment,
  usePendingDocSharesBox_AcceptDocShareMutation,
  usePendingDocSharesBox_PendingDocSharesQuery,
} from "./PendingDocSharesBox.generated.js";

// Mounting is gated by `MANAGE_QUOTES` from the outside (see Dashboard), so the
// query below only runs for permitted users; the box owns its own fetching and
// renders nothing when there are no pending shares.
export function PendingDocSharesBox() {
  const { t } = useTranslate(["Dashboard", "Global"]);
  const client = useApolloClient();
  const navigate = useNavigate();

  const pendingDocSharesQuery = usePendingDocSharesBox_PendingDocSharesQuery({
    client,
  });
  const pendingDocSharesResult = pendingDocSharesQuery.data?.pendingDocShares;
  const pendingDocShares =
    pendingDocSharesResult?.__typename === "PendingDocSharesResult"
      ? pendingDocSharesResult.pendingDocShares
      : [];

  const [acceptDocShare] = usePendingDocSharesBox_AcceptDocShareMutation({
    client,
  });

  const [acceptingShare, setAcceptingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);
  const [previewingShare, setPreviewingShare] =
    React.useState<PendingDocSharesBox_PendingDocShareFragment | null>(null);

  if (pendingDocShares.length === 0) {
    return null;
  }

  return (
    <CardContainer
      isExpandable
      isInitiallyClosed={false}
      itemCount={pendingDocShares.length}
      title={t("Documents shared with you", { ns: "Dashboard" })}
    >
      <List disablePadding>
        {pendingDocShares.map((pendingDocShare, index, array) => (
          <ProfileListItemButton
            key={pendingDocShare.id}
            avatar={
              <Avatar type="square" size="m">
                <DescriptionIcon />
              </Avatar>
            }
            primary={pendingDocShare.docTitle}
            secondary={t("Shared by {organisationName}", {
              ns: "Dashboard",
              organisationName: pendingDocShare.sharingSystemOrganisationTitle,
            })}
            onClick={() => setPreviewingShare(pendingDocShare)}
            ActionButton={
              <Button
                size="extra-small"
                variant="outlined"
                color="primary"
                onClick={e => {
                  // Accept is a distinct control: don't also open the preview.
                  e.stopPropagation();
                  setAcceptingShare(pendingDocShare);
                }}
              >
                {t("Accept", { ns: "Global" })}
              </Button>
            }
            listItemProps={{ divider: index < array.length - 1 }}
          />
        ))}
      </List>

      {previewingShare && (
        <PendingDocSharePreviewModal
          pendingDocShareId={previewingShare.id}
          handleClose={() => setPreviewingShare(null)}
          onAccept={() => setAcceptingShare(previewingShare)}
        />
      )}

      {acceptingShare && (
        <AcceptPendingDocShareModal
          pendingDocShare={acceptingShare}
          handleClose={() => setAcceptingShare(null)}
          handleAccepted={async (projectId, clientCrmOrganisationId) => {
            await acceptDocShare({
              variables: {
                input: {
                  id: acceptingShare.id,
                  acceptInProjectId: projectId,
                  // Only forward a CRM mapping when the share actually has a
                  // CLIENT actor to map; otherwise the backend would run a
                  // no-op CRM lookup/validation.
                  clientCrmOrganisationId: acceptingShare.clientActor
                    ? clientCrmOrganisationId
                    : null,
                },
              },
            });
            // Navigate to the freshly created project. This also unmounts the
            // dashboard (and the preview modal, when accepting from it), so the
            // now-deleted pending share is never re-queried — avoiding a
            // "pending doc share not found" error from a stale refetch.
            navigate(`/projects/${projectId}/overview`);
          }}
        />
      )}
    </CardContainer>
  );
}

function AcceptPendingDocShareModal({
  pendingDocShare,
  handleClose,
  handleAccepted,
}: {
  pendingDocShare: PendingDocSharesBox_PendingDocShareFragment;
  handleClose: () => void;
  handleAccepted: (
    projectId: string,
    clientCrmOrganisationId: string | null
  ) => Promise<void>;
}) {
  const { t } = useTranslate(["Dashboard"]);
  const { enqueueSnackbar } = useSnackbar();

  const clientActor = pendingDocShare.clientActor;

  const clientName = clientActor
    ? clientActor.companyName ||
      [clientActor.firstname, clientActor.familyname]
        .filter(Boolean)
        .join(" ") ||
      clientActor.email ||
      ""
    : "";

  // A CLIENT actor with a company name is a company contact; otherwise treat it
  // as an individual.
  const crmOrganisation =
    clientActor?.matchedCrmCompanyId != null && clientName
      ? { id: clientActor.matchedCrmCompanyId, title: clientName }
      : undefined;

  const initialClientPrefill: CrmCreateFormPrefill | undefined =
    clientActor && clientActor.matchedCrmCompanyId == null
      ? {
          companyName: clientActor.companyName ?? undefined,
          firstname: clientActor.firstname ?? undefined,
          familyname: clientActor.familyname ?? undefined,
          email: clientActor.email ?? undefined,
          contactType: clientActor.companyName ? "COMPANY" : "INDIVIDUAL",
        }
      : undefined;

  return (
    <CreateProjectModal
      title={t("Accept shared document", { ns: "Dashboard" })}
      pageName="Dashboard"
      desiredProjectInitialStatus="opportunity"
      crmOrganisation={crmOrganisation}
      initialClientPrefill={initialClientPrefill}
      handleClose={handleClose}
      handleComplete={async (projectId, crmOrganisationId) => {
        await handleAccepted(projectId, crmOrganisationId);
        enqueueSnackbar(t("Shared document accepted", { ns: "Dashboard" }));
      }}
    />
  );
}
